/**
 * StepAderencia — Aderência ao Escopo (após o Gate de Negócio).
 *
 * Momento de confiança para o cliente: mostra, de forma objetiva, O QUE ELE PEDIU
 * (escopo/jornadas) × O QUE JÁ GERAMOS (telas do Design Studio) e a % de aderência.
 * Validação de JORNADA (módulos/funcionalidades), não visual. Alimentado por
 * POST/GET /projects/{id}/design-studio/adherence (IA compara escopo × telas).
 *
 * Paleta C, estilos inline, pt-BR, a11y.
 */
import { useState } from 'react';
import { CheckCircle2, AlertTriangle, ArrowRight, Sparkles, ShieldCheck, Target, Wand2 } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { LoadingSpinner, MutationError, QueryError } from '@/components/ui/QueryState';
import { AiProgress } from '@/components/ui/AiProgress';
import { AvisosDaGeracao } from '@/components/DesignProgressPanel';
import { useAdherence, useComputeAdherence, useGenerateMissing } from '@/api/designStudio';
import { GateExportBar } from '../../gates/GateExportBar';
import { useJourney } from '../JourneyContext';

function pctColor(p) {
  if (p >= 90) return C.cyber;
  if (p >= 70) return C.digital ?? '#FD00D5';
  return C.hotLight ?? '#ff6b6b';
}

export function StepAderencia({ onNext, onBack }) {
  const { projectId } = useJourney();
  const adherenceQ = useAdherence(projectId);
  const compute = useComputeAdherence(projectId);
  const genMissing = useGenerateMissing(projectId);
  const [gapNote, setGapNote] = useState('');

  const data = adherenceQ.data;
  const computed = Boolean(data?.computed);
  const pct = Number(data?.coverage_pct ?? 0);
  const modules = Array.isArray(data?.modules) ? data.modules : [];
  const missing = Array.isArray(data?.missing) ? data.missing : [];
  const extra = Array.isArray(data?.extra_screens) ? data.extra_screens : [];
  const coveredCount = modules.filter((m) => m.covered).length;

  // Lacunas = módulos não cobertos + a lista `missing` da IA (deduplicado).
  const gapModules = Array.from(new Set([
    ...modules.filter((m) => !m.covered).map((m) => m.module),
    ...missing,
  ].filter(Boolean)));
  const hasGaps = gapModules.length > 0;

  // Objeto p/ exportar .md / PDF (reaproveita o GateExportBar).
  const exportGate = {
    num: '—',
    label: 'Aderência ao Escopo',
    projectName: undefined,
    projectId,
    description: `Aderência de jornada: ${pct}% (${coveredCount}/${modules.length} módulos, ${data?.screens_count ?? 0} telas). ${data?.verdict || ''}`,
    statusLabel: `${pct}% de aderência`,
    sections: [
      modules.length ? {
        title: 'Módulos do escopo × telas geradas',
        kind: 'list',
        items: modules.map((m) => `${m.covered ? '✅' : '⚠️'} **${m.module}**`
          + (m.screens?.length ? ` — Telas: ${m.screens.join(', ')}` : '')
          + (m.note ? ` (${m.note})` : '')),
      } : null,
      gapModules.length ? { title: 'Lacunas (módulos sem tela)', kind: 'list', items: gapModules } : null,
      extra.length ? { title: 'Telas sem base clara no escopo', kind: 'list', items: extra } : null,
    ].filter(Boolean),
  };

  function handleGenerateMissing() {
    genMissing.mutate({ modules: gapModules, note: gapNote.trim() || undefined });
  }

  return (
    <div>
      {/* Título */}
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Aderência ao Escopo
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          O que você pediu <strong style={{ color: C.neutral }}>×</strong> o que já geramos até
          aqui. Validação de <strong style={{ color: C.neutral }}>jornada</strong> (módulos e
          funcionalidades) — não do visual.
        </p>
      </div>

      {adherenceQ.isLoading && <LoadingSpinner label="Carregando validação…" />}
      {adherenceQ.error && <QueryError error={adherenceQ.error} onRetry={() => adherenceQ.refetch()} />}
      <MutationError error={compute.error} onDismiss={() => compute.reset()} />

      {/* Estado inicial: ainda não validado */}
      {!computed && !adherenceQ.isLoading && (
        <Card style={{ marginBottom: 20, textAlign: 'center', padding: 28 }}>
          <Target size={30} style={{ color: C.cyber, marginBottom: 10 }} aria-hidden="true" />
          <div style={{ fontSize: 15, fontWeight: 600, color: C.neutral, marginBottom: 6 }}>
            Valide a aderência do produto ao seu escopo
          </div>
          <p style={{ fontSize: 13, color: C.neutralMuted, maxWidth: 560, margin: '0 auto 16px', lineHeight: 1.55 }}>
            A IA compara os módulos/jornadas do seu escopo com as telas geradas no Design Studio
            e mostra a cobertura, módulo a módulo — o que está coberto e o que ainda falta.
          </p>
          <Btn variant="primary" size="lg" icon={Sparkles}
            onClick={() => compute.mutate()} disabled={compute.isPending} aria-busy={compute.isPending}>
            {compute.isPending ? 'Validando…' : 'Validar aderência ao escopo'}
          </Btn>
          <AiProgress busy={compute.isPending} label="Validando aderência ao escopo" />
        </Card>
      )}

      {/* Resultado */}
      {computed && (
        <>
          {/* Cabeçalho: % + veredito */}
          <Card style={{ marginBottom: 18 }}>
            <div className="flex items-center gap-4" style={{ flexWrap: 'wrap' }}>
              {/* Anel de % */}
              <div style={{
                width: 96, height: 96, borderRadius: '50%', flexShrink: 0,
                background: `conic-gradient(${pctColor(pct)} ${pct * 3.6}deg, ${C.blackBorder} 0deg)`,
                display: 'grid', placeItems: 'center',
              }}>
                <div style={{
                  width: 74, height: 74, borderRadius: '50%', background: C.blackElev ?? '#141414',
                  display: 'grid', placeItems: 'center',
                }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: pctColor(pct) }}>{pct}%</div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                  <ShieldCheck size={16} style={{ color: pctColor(pct) }} aria-hidden="true" />
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.neutral }}>Aderência de jornada</span>
                  <Badge color={pctColor(pct)} light>{coveredCount}/{modules.length} módulos</Badge>
                  {data?.screens_count != null && (
                    <Badge color={C.cyber} light>{data.screens_count} telas</Badge>
                  )}
                </div>
                <p style={{ fontSize: 13.5, color: C.neutralDim, margin: 0, lineHeight: 1.55 }}>
                  {data?.verdict || 'Comparação entre o escopo pedido e as telas geradas.'}
                </p>
                <div className="flex items-center gap-2" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                  <Btn variant="ghost" size="sm" icon={Sparkles}
                    onClick={() => compute.mutate()} disabled={compute.isPending} aria-busy={compute.isPending}>
                    {compute.isPending ? 'Revalidando…' : 'Revalidar'}
                  </Btn>
                  <GateExportBar gate={exportGate} />
                </div>
              </div>
            </div>
          </Card>

          {/* Matriz de módulos */}
          <Card style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 12, letterSpacing: '0.04em' }}>
              MÓDULOS DO ESCOPO × TELAS GERADAS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {modules.map((m, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 12px',
                  borderRadius: 8, background: C.blackElev2,
                  border: `1px solid ${m.covered ? `${C.cyber}33` : `${C.hotLight}44`}`,
                }}>
                  {m.covered
                    ? <CheckCircle2 size={16} style={{ color: C.cyber, flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                    : <AlertTriangle size={16} style={{ color: C.hotLight, flexShrink: 0, marginTop: 2 }} aria-hidden="true" />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: C.neutral }}>{m.module}</div>
                    {Array.isArray(m.screens) && m.screens.length > 0 && (
                      <div style={{ fontSize: 12, color: C.neutralMuted, marginTop: 3 }}>
                        Telas: {m.screens.join(' · ')}
                      </div>
                    )}
                    {m.note && (
                      <div style={{ fontSize: 12, color: m.covered ? C.neutralDim : C.hotLight, marginTop: 3 }}>
                        {m.note}
                      </div>
                    )}
                  </div>
                  <Badge color={m.covered ? C.cyber : C.hotLight} light>
                    {m.covered ? 'COBERTO' : 'FALTA'}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>

          {/* Lacunas e extras */}
          {(missing.length > 0 || extra.length > 0) && (
            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', marginBottom: 18 }}>
              {missing.length > 0 && (
                <Card style={{ borderColor: `${C.hotLight}55` }}>
                  <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                    <AlertTriangle size={14} style={{ color: C.hotLight }} aria-hidden="true" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.hotLight }}>Módulos do escopo sem tela</span>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: C.neutralDim, fontSize: 12.5, lineHeight: 1.7 }}>
                    {missing.map((mm, i) => <li key={i}>{mm}</li>)}
                  </ul>
                </Card>
              )}
              {extra.length > 0 && (
                <Card style={{ borderColor: `${C.digital ?? '${C.digital}'}55` }}>
                  <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                    <AlertTriangle size={14} style={{ color: C.digital ?? '#FD00D5' }} aria-hidden="true" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.digital ?? '#FD00D5' }}>
                      Telas sem base clara no escopo
                    </span>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: C.neutralDim, fontSize: 12.5, lineHeight: 1.7 }}>
                    {extra.map((ee, i) => <li key={i}>{ee}</li>)}
                  </ul>
                </Card>
              )}
            </div>
          )}
          {/* Fechar lacunas → gerar SÓ o que falta (chegar a 100%) */}
          {hasGaps && (
            <Card style={{ marginBottom: 18, borderColor: `${C.cyber}44` }}>
              <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                <Wand2 size={16} style={{ color: C.cyber }} aria-hidden="true" />
                <span style={{ fontSize: 14, fontWeight: 700, color: C.neutral }}>Chegar a 100% — gerar só o que falta</span>
              </div>
              <p style={{ fontSize: 12.5, color: C.neutralMuted, margin: '0 0 12px', lineHeight: 1.55 }}>
                Faltam <strong style={{ color: C.hotLight }}>{gapModules.length}</strong> módulo(s). Se algo ficou
                de fora porque não estava na especificação, <strong style={{ color: C.neutral }}>complemente abaixo</strong> —
                isso vira nota de escopo do projeto. A IA gera <strong style={{ color: C.neutral }}>apenas as telas
                faltantes</strong> e anexa às existentes (sem refazer o resto).
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {gapModules.map((g, i) => (
                  <span key={i} style={{ fontSize: 11.5, color: C.hotLight, background: `${C.hotLight}14`,
                    border: `1px solid ${C.hotLight}44`, borderRadius: 999, padding: '3px 10px' }}>{g}</span>
                ))}
              </div>
              <MutationError error={genMissing.error} onDismiss={() => genMissing.reset()} />
              {/* teto/falha que interrompeu as faltantes aparece aqui, com o que faltou */}
              <AvisosDaGeracao avisos={genMissing.data?.avisos} />
              <textarea
                value={gapNote}
                onChange={(e) => setGapNote(e.target.value)}
                placeholder="Opcional: complemente a especificação do que faltou (ex.: 'silenciar posts/histórias: ação no menu do post que oculta o conteúdo do autor sem deixar de seguir')…"
                rows={3}
                aria-label="Complemento de especificação para as lacunas"
                style={{ width: '100%', padding: '10px 14px', backgroundColor: C.black,
                  border: `1px solid ${C.blackBorder}`, borderRadius: 6, color: C.neutral, fontSize: 13,
                  resize: 'vertical', lineHeight: 1.5, marginBottom: 12, boxSizing: 'border-box' }}
              />
              <Btn variant="primary" size="md" icon={Wand2}
                onClick={handleGenerateMissing} disabled={genMissing.isPending} aria-busy={genMissing.isPending}>
                {genMissing.isPending ? 'Gerando as telas que faltam…' : `Gerar ${gapModules.length} tela(s) faltante(s)`}
              </Btn>
              <AiProgress busy={genMissing.isPending} label="Gerando as telas que faltam" />
              <p style={{ fontSize: 11.5, color: C.neutralMuted, margin: '10px 0 0' }}>
                Ao terminar, clique em <strong style={{ color: C.neutral }}>Revalidar</strong> acima para recalcular a aderência.
              </p>
            </Card>
          )}
        </>
      )}

      {/* Navegação */}
      <div className="flex items-center justify-between">
        <Btn variant="ghost" size="md" onClick={onBack}>Voltar</Btn>
        <Btn variant="primary" size="lg" icon={ArrowRight} onClick={onNext}
          aria-label="Continuar para a próxima etapa">
          Continuar
        </Btn>
      </div>
    </div>
  );
}
